/**
 * Schedule form component — CoordioCore form-page-pattern.
 *
 * Layout: PageFormLayout (ShellHeader + SectionSideNav grid + panel sections)
 * Fields: @coordio/ui FormField (label + ⓘ tooltip + error + required asterisk)
 * Cards:  @coordio/ui FormSection (title + description + bordered body)
 * Toggle: @coordio/ui FormChoiceField (label + Switch in horizontal row)
 */

// React Imports
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Calendar, Clock, Settings } from 'lucide-react';

// @coordio/ui Imports
import { CoordioDropdown, FormField, Input, Switch, Textarea } from '@coordio/ui';
import { FormSection } from '@coordio/ui/layout';

// Component Imports
import { PageFormLayout } from '../layouts/PageFormLayout';
import { scheduleService } from '../../services/scheduleService';
import { agentService } from '../../services/agentService';
import { useNotificationStore } from '../../store/notificationStore';
import { validateCronExpression } from '../../utils/cronValidation';
import { CronBuilder } from './CronBuilder';

// Types
import type {
  CreateScheduleRequest,
  UpdateScheduleRequest,
} from '../../types/schedule';
import type { ScheduleType } from '../../types/schedule';
import { ScheduleTypes } from '../../types/schedule';
import type { AgentDefinitionDto } from '../../types/agent';

const scheduleTypeOptions = [
  { label: 'Cron', value: ScheduleTypes.Cron },
  { label: 'Interval', value: ScheduleTypes.Interval },
  { label: 'One-Time', value: ScheduleTypes.OneTime },
];

/**
 * Schedule form component for creating and editing schedules.
 */
export const ScheduleForm = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEdit = !!id;
  const { showNotification } = useNotificationStore();

  const [agents, setAgents] = useState<AgentDefinitionDto[]>([]);
  const [loading, setLoading] = useState(isEdit);
  const [loadingAgents, setLoadingAgents] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form fields
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [agentDefinitionId, setAgentDefinitionId] = useState('');
  const [scheduleType, setScheduleType] = useState<ScheduleType>(ScheduleTypes.Cron);
  const [cronExpression, setCronExpression] = useState('');
  const [cronError, setCronError] = useState<string | null>(null);
  const [scheduledDateTime, setScheduledDateTime] = useState('');
  const [interval, setInterval] = useState('');
  const [timeZone, setTimeZone] = useState('UTC');
  const [isActive, setIsActive] = useState(true);
  const [parametersJson, setParametersJson] = useState('');
  const [maxRetries, setMaxRetries] = useState<number | ''>('');
  const [retryDelay, setRetryDelay] = useState('');

  const agentOptions = agents.map((agent) => ({ label: agent.name, value: agent.id }));

  useEffect(() => {
    loadAgents();
    if (isEdit && id) {
      loadSchedule();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isEdit]);

  const loadAgents = async () => {
    try {
      const result = await agentService.getAgents({ pageSize: 1000, isActive: true });
      setAgents(result.items);
    } catch (err: any) {
      showNotification(err.message || 'Failed to load agents', 'error');
    } finally {
      setLoadingAgents(false);
    }
  };

  const loadSchedule = async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const data = await scheduleService.getSchedule(id);
      setName(data.name);
      setDescription(data.description || '');
      setAgentDefinitionId(data.agentDefinitionId);
      setScheduleType(data.scheduleType);
      setCronExpression(data.cronExpression || '');
      setScheduledDateTime(data.scheduledDateTime || '');
      setInterval(data.interval || '');
      setTimeZone(data.timeZone || 'UTC');
      setIsActive(data.isActive);
      setParametersJson(data.parametersJson || '');
      setMaxRetries(data.maxRetries || '');
      setRetryDelay(data.retryDelay || '');
    } catch (err: any) {
      const msg = err.response?.data?.message || 'Failed to load schedule';
      setError(msg);
      showNotification(msg, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    setError(null);

    if (!name.trim()) {
      setError('Name is required');
      return;
    }

    if (!agentDefinitionId) {
      setError('Agent is required');
      return;
    }

    if (scheduleType === ScheduleTypes.Cron) {
      if (!cronExpression.trim()) {
        setError('Cron expression is required for Cron schedule type');
        setCronError('Cron expression is required');
        return;
      }
      if (!validateCronExpression(cronExpression.trim())) {
        setError('Invalid cron expression. Please check the format.');
        setCronError('Invalid cron expression format');
        return;
      }
      setCronError(null);
    }

    if (scheduleType === ScheduleTypes.OneTime && !scheduledDateTime) {
      setError('Scheduled date/time is required for One-Time schedule type');
      return;
    }

    if (scheduleType === ScheduleTypes.Interval && !interval.trim()) {
      setError('Interval is required for Interval schedule type');
      return;
    }

    setSaving(true);
    try {
      const request: CreateScheduleRequest | UpdateScheduleRequest = {
        name: name.trim(),
        description: description.trim() || undefined,
        agentDefinitionId,
        scheduleType,
        cronExpression: scheduleType === ScheduleTypes.Cron ? cronExpression.trim() : undefined,
        scheduledDateTime: scheduleType === ScheduleTypes.OneTime ? scheduledDateTime : undefined,
        interval: scheduleType === ScheduleTypes.Interval ? interval.trim() : undefined,
        timeZone,
        isActive,
        parametersJson: parametersJson.trim() || undefined,
        maxRetries: maxRetries !== '' ? Number(maxRetries) : undefined,
        retryDelay: retryDelay.trim() || undefined,
      };

      if (isEdit && id) {
        await scheduleService.updateSchedule(id, request);
        showNotification('Schedule updated successfully', 'success');
      } else {
        await scheduleService.createSchedule(request);
        showNotification('Schedule created successfully', 'success');
      }
      navigate('/schedules');
    } catch (err: any) {
      const msg = err.response?.data?.message || err.message || 'Failed to save schedule';
      setError(msg);
      showNotification(msg, 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <PageFormLayout
      title={isEdit ? 'Edit Schedule' : 'Create Schedule'}
      subtitle="Configure automated agent execution with cron, interval, or one-time scheduling."
      data-testid="schedule-form-page"
      loading={loading || loadingAgents}
      error={error ?? undefined}
      onErrorDismiss={() => setError(null)}
      backTo={{ label: 'Back to List', href: '/schedules' }}
      actions={{
        onSave: handleSubmit,
        onCancel: () => navigate('/schedules'),
        saveLabel: isEdit ? 'Update' : 'Create',
        saving,
      }}
      navGroups={[
        {
          title: 'PROFILE',
          items: [
            { id: 'details', label: 'Details', icon: <Calendar size={16} /> },
          ],
        },
        {
          title: 'CONFIGURATION',
          items: [
            { id: 'timing', label: 'Timing', icon: <Clock size={16} /> },
            { id: 'options', label: 'Options', icon: <Settings size={16} /> },
          ],
        },
      ]}
      sections={[
        {
          id: 'details',
          title: 'Details',
          description: 'Name, description, and the agent this schedule triggers.',
          icon: <Calendar size={20} />,
          content: (
            <FormSection
              title="Schedule identity"
              description="Give this schedule a descriptive name and choose which agent it runs."
            >
              <div className="grid gap-5 items-end sm:grid-cols-2">
                <FormField
                  label="Name"
                  required
                  description="Unique name for this schedule."
                  data-testid="schedule-name"
                >
                  <Input
                    className="w-full"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    data-testid="schedule-name"
                  />
                </FormField>

                <FormField
                  label="Agent"
                  required
                  description="The agent definition this schedule triggers."
                  data-testid="schedule-agent"
                >
                  <CoordioDropdown
                    options={agentOptions}
                    value={agentDefinitionId}
                    onChange={(val) => setAgentDefinitionId(val ?? '')}
                    clearable={false}
                    data-testid="schedule-agent"
                  />
                </FormField>
              </div>

              <div className="mt-5">
                <FormField
                  label="Description"
                  description="Optional context about what this schedule does."
                  data-testid="schedule-description"
                >
                  <Textarea
                    className="w-full"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    rows={3}
                    data-testid="schedule-description"
                  />
                </FormField>
              </div>
            </FormSection>
          ),
        },
        {
          id: 'timing',
          title: 'Timing',
          description: 'Choose the schedule type and configure when the agent runs.',
          icon: <Clock size={20} />,
          content: (
            <FormSection
              title="Schedule timing"
              description="Select a schedule type and configure the timing parameters."
            >
              <div className="grid gap-5 items-end sm:grid-cols-2">
                <FormField
                  label="Schedule Type"
                  required
                  description="Determines whether the schedule uses cron, interval, or one-time execution."
                  data-testid="schedule-type"
                >
                  <CoordioDropdown
                    options={scheduleTypeOptions}
                    value={scheduleType}
                    onChange={(val) => setScheduleType((val ?? ScheduleTypes.Cron) as ScheduleType)}
                    clearable={false}
                    data-testid="schedule-type"
                  />
                </FormField>

                <FormField
                  label="Time Zone"
                  description="IANA time zone for this schedule (e.g., UTC, America/New_York, Europe/London)."
                  data-testid="schedule-timeZone"
                >
                  <Input
                    className="w-full"
                    value={timeZone}
                    onChange={(e) => setTimeZone(e.target.value)}
                    data-testid="schedule-timeZone"
                  />
                </FormField>
              </div>

              {scheduleType === ScheduleTypes.Cron && (
                <div className="mt-5">
                  <CronBuilder
                    value={cronExpression}
                    onChange={(value) => {
                      setCronExpression(value);
                      setCronError(null);
                      if (value && !validateCronExpression(value)) {
                        setCronError('Invalid cron expression format');
                      }
                    }}
                    error={!!cronError}
                    helperText={cronError || 'Standard cron format (minute hour day month day-of-week)'}
                  />
                </div>
              )}

              {scheduleType === ScheduleTypes.OneTime && (
                <div className="mt-5">
                  <FormField
                    label="Scheduled Date/Time"
                    required
                    description="The exact date and time for this one-time execution."
                    data-testid="schedule-scheduledDateTime"
                  >
                    <Input
                      className="w-full"
                      type="datetime-local"
                      value={scheduledDateTime}
                      onChange={(e) => setScheduledDateTime(e.target.value)}
                      data-testid="schedule-scheduledDateTime"
                    />
                  </FormField>
                </div>
              )}

              {scheduleType === ScheduleTypes.Interval && (
                <div className="mt-5">
                  <FormField
                    label="Interval"
                    required
                    description="ISO 8601 duration format (e.g., PT1H for 1 hour, PT30M for 30 minutes)."
                    data-testid="schedule-interval"
                  >
                    <Input
                      className="w-full"
                      placeholder="PT1H (1 hour), PT30M (30 minutes), etc."
                      value={interval}
                      onChange={(e) => setInterval(e.target.value)}
                      data-testid="schedule-interval"
                    />
                  </FormField>
                </div>
              )}
            </FormSection>
          ),
        },
        {
          id: 'options',
          title: 'Options',
          description: 'Advanced settings for parameters, retries, and activation.',
          icon: <Settings size={20} />,
          content: (
            <FormSection
              title="Advanced options"
              description="Configure input payload, retry behavior, and whether this schedule is active."
            >
              <FormField
                label="Parameters JSON"
                description="Optional JSON parameters to pass to the agent at execution time."
                data-testid="schedule-parametersJson"
              >
                <Textarea
                  className="w-full"
                  rows={4}
                  placeholder='{"key": "value"}'
                  value={parametersJson}
                  onChange={(e) => setParametersJson(e.target.value)}
                  data-testid="schedule-parametersJson"
                />
              </FormField>

              <div className="mt-5 grid gap-5 items-end sm:grid-cols-2">
                <FormField
                  label="Max Retries"
                  description="Maximum number of retries on failure."
                  data-testid="schedule-maxRetries"
                >
                  <Input
                    className="w-full"
                    type="number"
                    value={maxRetries}
                    onChange={(e) => setMaxRetries(e.target.value === '' ? '' : Number(e.target.value))}
                    data-testid="schedule-maxRetries"
                  />
                </FormField>

                <FormField
                  label="Retry Delay"
                  description="ISO 8601 duration format (e.g., PT5M for 5 minutes)."
                  data-testid="schedule-retryDelay"
                >
                  <Input
                    className="w-full"
                    placeholder="PT5M"
                    value={retryDelay}
                    onChange={(e) => setRetryDelay(e.target.value)}
                    data-testid="schedule-retryDelay"
                  />
                </FormField>
              </div>

              <div className="mt-5">
                <div className="ui-form-choice-field">
                  <div className="ui-form-choice-field__row">
                    <div className="ui-form-choice-field__control">
                      <Switch
                        checked={isActive}
                        onCheckedChange={setIsActive}
                        data-testid="schedule-isActive"
                      />
                    </div>
                    <div className="ui-form-choice-field__label-cell">
                      <span className="ui-form-choice-field__label">Active</span>
                    </div>
                  </div>
                </div>
              </div>
            </FormSection>
          ),
        },
      ]}
    />
  );
};
